import { APP_INITIALIZER, ApplicationConfig, provideBrowserGlobalErrorListeners, provideZoneChangeDetection } from '@angular/core';
import { provideRouter } from '@angular/router';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { PrimeNG, providePrimeNG } from 'primeng/config';
import { definePreset } from '@primeuix/themes';
import { ConfirmationService, MessageService } from 'primeng/api';
import Aura from '@primeuix/themes/aura';

import { routes } from './app.routes';
import { GlobalSettingsService } from './shared/services/global-settings.service';
import { provideHttpClient, withInterceptorsFromDi, HTTP_INTERCEPTORS } from '@angular/common/http';
import { InteractionType, PublicClientApplication } from '@azure/msal-browser';
import { environment } from '../environments/environment';
import { MSAL_INSTANCE, MSAL_INTERCEPTOR_CONFIG, MsalInterceptor, MsalService, MsalGuard, MsalBroadcastService } from '@azure/msal-angular';

// ---------- THEMES ----------
const MyTheme = definePreset(Aura, {
  options: { darkModeSelector: false },
  primitive: {
    surface: {
      0: '#ffffff', 50: '#f9fafb', 100: '#f3f4f6', 200: '#e5e7eb', 300: '#d1d5db',
      400: '#9ca3af', 500: '#6b7280', 600: '#4b5563', 700: '#374151', 800: '#1f2937', 900: '#111827'
    }
  },
  semantic: {
    primary: {
      50: '#58a2d9', 100: '#58a2d9', 200: '#4899d5', 300: '#1b5178', 400: '#1b5178',
      500: '#2a7cb8', 600: '#2671a8', 700: '#236698', 800: '#1f5c88', 900: '#1b5178'
    }
  }
});

function provideTheme(config: PrimeNG) {
  return () => {
    config.theme.set({ preset: MyTheme, options: { darkModeSelector: false } });
  };
}

export function msalInstanceFactory() {
  return new PublicClientApplication({
    auth: {
      clientId: environment.azureADClientId,
      authority: environment.azureADAuthority,
      redirectUri: environment.azureADRedirectUri
    },
    cache: { cacheLocation: 'localStorage', storeAuthStateInCookie: false }
  });
}

export function msalInterceptorConfigFactory() {
  const protectedResourceMap = new Map<string, string[]>();
  protectedResourceMap.set(environment.apiBaseUrl + "/", [environment.apiScope]);
  return { interactionType: InteractionType.Redirect, protectedResourceMap };
}

export function msalInitializer(msalService: MsalService) {
  return () => msalService.instance.initialize();
}

export const appConfig: ApplicationConfig = {
  providers: [
    provideHttpClient(withInterceptorsFromDi()),
    provideRouter(routes),
    provideBrowserGlobalErrorListeners(),
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideAnimationsAsync(),

    MessageService,
    ConfirmationService,
    GlobalSettingsService,
    providePrimeNG({ theme: { preset: MyTheme, options: { darkModeSelector: false } } }),
    { provide: APP_INITIALIZER, useFactory: provideTheme, deps: [PrimeNG], multi: true },

    { provide: MSAL_INSTANCE, useFactory: msalInstanceFactory },
    { provide: MSAL_INTERCEPTOR_CONFIG, useFactory: msalInterceptorConfigFactory },
    { provide: HTTP_INTERCEPTORS, useClass: MsalInterceptor, multi: true },
    MsalService,
    MsalGuard,
    MsalBroadcastService,
    { provide: APP_INITIALIZER, useFactory: msalInitializer, deps: [MsalService], multi: true },
  ]
};
